import { z } from 'zod';
import { isOfferedUspsParcelTemplate } from '../../../data/js/usps-parcel-templates';

/**
 * A lightweight listing summary for the discount manage-listings drawer:
 * enough to show a thumbnail, title, and price for listings already attached
 * to a discount (looked up by id).
 */
export const SellerListingSummarySchema = z.object({
  id: z.number(),
  title: z.string(),
  price: z.number(),
  photos: z.array(z.object({ url: z.string() })),
});

/**
 * Success response for GET /api/seller/listings/by-ids
 */
export const GetSellerListingsByIdsSuccessSchema = z.object({
  success: z.literal(true),
  listings: z.array(SellerListingSummarySchema),
});

export const SellerListingsErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export const GetSellerListingsByIdsResponseSchema = z.union([
  GetSellerListingsByIdsSuccessSchema,
  SellerListingsErrorSchema,
]);

export type SellerListingSummary = z.infer<typeof SellerListingSummarySchema>;
export type GetSellerListingsByIdsResponse = z.infer<typeof GetSellerListingsByIdsResponseSchema>;

// ---------------------------------------------------------------------------
// GET /api/seller/listings — the seller's own listings, from the database
//
// Deliberately NOT Elasticsearch. A seller's own listings are a small, highly
// selective set, and the index cannot serve this page at all: ES excludes
// status 0, so archived listings — the ones a seller most needs to find in
// order to relist them — are invisible there. Reading the rows directly also
// gives an exact total and a stable order, neither of which the index offers
// past its 10,000-result window.
// ---------------------------------------------------------------------------

/** Statuses this page can filter to. `all` means every status the seller owns. */
export const SellerListingStatusFilterSchema = z.enum([
  'all',
  'listed',
  'popular',
  'pending',
  'sold',
  'archived',
]);

export const GetSellerListingsQuerySchema = z.object({
  status: SellerListingStatusFilterSchema.optional().default('all'),
  /**
   * When present, each row reports whether it already carries this coupon, and
   * the response reports how many of the current filter do. That is what lets
   * the manage-listings drawer be a searchable list of the whole store rather
   * than a chip picker that could only hold a couple of hundred.
   */
  couponId: z.coerce.number().int().positive().optional(),
  /**
   * The timed-discount equivalent of `couponId`, and mutually exclusive with it.
   *
   * A timed discount (DISCOUNT_RULES) attaches listings through its own junction
   * table, so the drawer needs the same per-row "is this one on it?" answer from
   * a different table. Without this the drawer had nothing to read for a rule,
   * which is why it refused to open on one at all and showed a dead
   * "managed from the discount itself" line instead.
   *
   * Naming both is a client bug rather than a merge: whichever the rest of the
   * handler reads first would silently win, so the request is rejected.
   */
  ruleId: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
  // Capped so a crafted value cannot ask for the whole table in one response.
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  q: z.string().trim().max(200).optional(),
}).refine((v) => !(v.couponId && v.ruleId), {
  message: 'Name either couponId or ruleId, not both',
  path: ['ruleId'],
});

export const SellerListingRowSchema = z.object({
  id: z.number(),
  title: z.string(),
  price: z.number(),
  status: z.number(),
  categoryId: z.number().nullable(),
  categoryGroupId: z.number().nullable(),
  createdAt: z.string().nullable(),
  photos: z.array(z.object({ url: z.string(), thumbUrl: z.string().optional() })),
  /** Only present when the request named a coupon or a timed-discount rule. */
  attached: z.boolean().optional(),
  /**
   * True when a DIFFERENT automatic coupon, running or paused, holds this
   * listing, so it cannot take another (utils/coupon-occupancy.ts). Only
   * present when the request named a coupon.
   */
  hasOtherCoupon: z.boolean().optional(),
  /**
   * True when a DIFFERENT active timed discount owns this listing.
   *
   * Two rules on one listing compound: each step mints its own coupon off the
   * listing's current price, so a 5%-weekly and a 10%-weekly rule together walk
   * a price down faster than either one describes, and neither is wrong on its
   * own terms. Only present when the request named a rule.
   */
  hasOtherRule: z.boolean().optional(),
  /**
   * True when smart pricing is running this listing. The rules engine skips such
   * listings on every step (apply-discount-rules-engine.ts checks SMART_PRICING
   * before anything else), so attaching one produces a row that silently never
   * discounts. Saying so up front beats a seller wondering why it never moved.
   */
  hasSmartPricing: z.boolean().optional(),
  /**
   * True when an automatic coupon (not this rule's own) is on the listing. The
   * rule's step coupon cannot go on beside it, so the rule does not change this
   * listing's price until that coupon is gone. The listing can still join the
   * rule. Only present when the request named a rule.
   */
  hasAutoCoupon: z.boolean().optional(),
  /**
   * Set when the listing is held for an admin's review (utils/listing-review.ts):
   * 'pending' until decided, 'rejected' after a no. Absent for every other
   * listing. The row says so instead of a bare "Not Active".
   */
  review: z.enum(['pending', 'rejected']).optional(),
});

/** One entry per status the seller actually has, so the sidebar can show counts. */
export const SellerListingStatusCountsSchema = z.object({
  all: z.number(),
  listed: z.number(),
  popular: z.number(),
  pending: z.number(),
  sold: z.number(),
  archived: z.number(),
});

export const GetSellerListingsSuccessSchema = z.object({
  success: z.literal(true),
  listings: z.array(SellerListingRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
  counts: SellerListingStatusCountsSchema,
});

export const GetSellerListingsResponseSchema = z.union([
  GetSellerListingsSuccessSchema,
  SellerListingsErrorSchema,
]);

// ---------------------------------------------------------------------------
// POST /api/seller/listings/bulk-status — delist or relist a selection
// ---------------------------------------------------------------------------

export const BulkListingStatusActionSchema = z.enum(['delist', 'relist']);

export const BulkListingStatusBodySchema = z.object({
  action: BulkListingStatusActionSchema,
  // Bounded: this endpoint takes an explicit selection, and a selection is
  // something a person ticked. Anything larger is a client bug or an attack,
  // and each id costs an index write.
  listingIds: z.array(z.number().int().positive()).min(1).max(200),
});

export const BulkListingStatusSuccessSchema = z.object({
  success: z.literal(true),
  action: BulkListingStatusActionSchema,
  /** Listings whose status actually changed. */
  changed: z.number(),
  /** Owned, but in a state this action cannot touch (a sale in progress, already there). Includes `held`. */
  skipped: z.number(),
  /**
   * Of `skipped`, the listings held for admin review or turned down, which only
   * an admin can put on sale. Optional so a client and server deployed a moment
   * apart still agree.
   */
  held: z.number().optional(),
  /** Ids that are not this seller's, or do not exist. Never named back to the caller. */
  notFound: z.number(),
  /** Rolled back because the search index write failed. */
  failed: z.number(),
});

export const BulkListingStatusResponseSchema = z.union([
  BulkListingStatusSuccessSchema,
  SellerListingsErrorSchema,
]);

/**
 * A USPS packaging token the account offers. "My own box" is not one: across
 * many listings it would mean "keep whatever size each one has", which is not a
 * change anyone needs a bulk action for.
 */
const OfferedParcelTemplateSchema = z
  .string()
  .refine(isOfferedUspsParcelTemplate, { message: 'Pick a USPS packaging' });

/**
 * POST /api/seller/listings/bulk-packaging
 *
 * `selected` is a ticked selection from My Listings, bounded like bulk-status.
 * `live` is every live listing the seller has, from the shipping settings page
 * right after they save a new default; it is resolved on the server so a seller
 * with thousands of listings never sends their ids.
 */
export const BulkListingPackagingBodySchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('selected'),
    parcelTemplate: OfferedParcelTemplateSchema,
    listingIds: z.array(z.number().int().positive()).min(1).max(200),
  }),
  z.object({
    scope: z.literal('live'),
    parcelTemplate: OfferedParcelTemplateSchema,
  }),
]);

export const BulkListingPackagingSuccessSchema = z.object({
  success: z.literal(true),
  /** Listings now in the packaging that were not before. */
  changed: z.number(),
  /** Already in that packaging. */
  unchanged: z.number(),
  /** Owned, but packaging is not theirs to change now: sold, a sale in progress, a vehicle, local pickup only. */
  skipped: z.number(),
  /** Ids that are not this seller's, or do not exist. Never named back to the caller. */
  notFound: z.number(),
});

export const BulkListingPackagingResponseSchema = z.union([
  BulkListingPackagingSuccessSchema,
  SellerListingsErrorSchema,
]);

/** GET /api/seller/listings/bulk-packaging?parcelTemplate=… */
export const BulkListingPackagingCountQuerySchema = z.object({
  parcelTemplate: OfferedParcelTemplateSchema,
});

export const BulkListingPackagingCountSuccessSchema = z.object({
  success: z.literal(true),
  /** Live listings whose packaging the seller can change. */
  live: z.number(),
  /** Of those, the ones in some other packaging, or none. */
  differing: z.number(),
});

export const BulkListingPackagingCountResponseSchema = z.union([
  BulkListingPackagingCountSuccessSchema,
  SellerListingsErrorSchema,
]);

export type BulkListingPackagingBody = z.infer<typeof BulkListingPackagingBodySchema>;
export type BulkListingPackagingSuccess = z.infer<typeof BulkListingPackagingSuccessSchema>;
export type BulkListingPackagingResponse = z.infer<typeof BulkListingPackagingResponseSchema>;
export type BulkListingPackagingCountResponse = z.infer<typeof BulkListingPackagingCountResponseSchema>;

export type SellerListingStatusFilter = z.infer<typeof SellerListingStatusFilterSchema>;
export type SellerListingRow = z.infer<typeof SellerListingRowSchema>;
export type SellerListingStatusCounts = z.infer<typeof SellerListingStatusCountsSchema>;
export type GetSellerListingsSuccess = z.infer<typeof GetSellerListingsSuccessSchema>;
export type GetSellerListingsResponse = z.infer<typeof GetSellerListingsResponseSchema>;
export type BulkListingStatusAction = z.infer<typeof BulkListingStatusActionSchema>;
export type BulkListingStatusBody = z.infer<typeof BulkListingStatusBodySchema>;
export type BulkListingStatusSuccess = z.infer<typeof BulkListingStatusSuccessSchema>;
export type BulkListingStatusResponse = z.infer<typeof BulkListingStatusResponseSchema>;
export type GetSellerListingsByIdsSuccess = z.infer<typeof GetSellerListingsByIdsSuccessSchema>;
export type SellerListingsError = z.infer<typeof SellerListingsErrorSchema>;
export type GetSellerListingsQuery = z.infer<typeof GetSellerListingsQuerySchema>;
